import React, { useCallback, useEffect, useState } from 'react';
import { secureFetch } from '../../supabaseService';
import ContractHmrPanel from './ContractHmrPanel';

/**
 * Operator home: list active contract bookings and log daily HMR.
 * Separate from spot assignments / daily attendance report.
 */
export default function OperatorContractHmrCard() {
  const [bookings, setBookings] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/operator/contract-bookings');
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load contract bookings');
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setBookings(items);
      setSelectedId((prev) => {
        if (prev && items.some((b) => b.id === prev)) return prev;
        return items[0]?.id || '';
      });
    } catch (err) {
      setError(err?.message || 'Could not load contract bookings');
      setBookings([]);
      setSelectedId('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = bookings.find((b) => b.id === selectedId) || null;
  const machineLabel = selected?.machines?.machine_id
    || selected?.machines?.name
    || selected?.machine_id
    || '—';

  return (
    <div style={{
      background: 'rgba(14, 40, 64, 0.9)',
      border: '1px solid rgba(56, 189, 248, 0.25)',
      borderRadius: '12px',
      padding: '14px',
      marginBottom: '16px',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: '#7dd3fc', fontSize: '13px', fontWeight: 700 }}>
          Contract HMR co-sign
        </h3>
        <button
          type="button"
          onClick={load}
          style={{
            background: 'transparent',
            border: '1px solid rgba(125, 211, 252, 0.35)',
            color: '#7dd3fc',
            borderRadius: '8px',
            padding: '4px 10px',
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <p style={{ margin: '0 0 10px', color: '#8896a8', fontSize: '11px' }}>
        Log the physical hour-meter reading for contract bookings. Spot daily report and attendance are unchanged.
      </p>

      {error && (
        <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>
      )}

      {bookings.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>No active contract bookings assigned.</p>
      ) : (
        <>
          <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '8px' }}>
            Contract booking
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: '4px',
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid rgba(201,168,76,0.3)',
                background: 'rgba(0,0,0,0.35)',
                color: '#e8e0d0',
              }}
            >
              {bookings.map((b) => (
                <option key={b.id} value={b.id}>
                  {(b.booking_ref || b.id).slice(0, 16)} · {b.machines?.machine_id || b.machines?.name || 'Machine'}
                </option>
              ))}
            </select>
          </label>
          {selected && (
            <p style={{ color: '#c9a84c', fontSize: '11px', margin: '0 0 8px' }}>
              {machineLabel} · {selected.status} · {selected.start_date || '—'} → {selected.end_date || '—'}
            </p>
          )}
          {selected && (
            <ContractHmrPanel booking={selected} role="operator" allowLog />
          )}
        </>
      )}
    </div>
  );
}
